/**
 * @author       denys.yefremov@startupsoft.co (Denys Yefremov)
 * @copyright    Copyright (c) 2022, Zumper
 */

import type { PayloadAction } from '@reduxjs/toolkit'
import { nullReducer, payloadReducer } from '@zcode/reducers'
import type { SchoolDistrict, SchoolDistrictLevels, SchoolLevelsWidget } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { combineReducers } from 'redux'
import { handleActions } from 'redux-actions'
import {
  CLEAR_SCHOOL_WIDGET_DATA,
  CLEAR_SCHOOL_WIDGET_SCHOOLS,
  SELECT_DISTRICT,
  SELECT_SCHOOL_LEVEL,
  SET_SCHOOL_WIDGET_DATA,
  SET_SCHOOL_WIDGET_SCHOOLS,
} from '../constants/index'
import { districtMetaReducer, schoolsMetaReducer } from './meta'

type SchoolDistricts = SchoolDistrict[]
type SelectedDistrict = SchoolDistrict | null

const schoolWidgetReducer = combineReducers({
  schoolDistricts: handleActions<SchoolDistricts, PayloadAction<SchoolDistricts>>(
    {
      [SET_SCHOOL_WIDGET_DATA]: payloadReducer,
      [CLEAR_SCHOOL_WIDGET_DATA]: () => [],
    },
    []
  ),
  schoolDistrictLevels: handleActions<
    MaybeEmpty<SchoolDistrictLevels>,
    PayloadAction<SchoolDistrictLevels>
  >(
    {
      [SET_SCHOOL_WIDGET_SCHOOLS]: (state, action) => {
        const { payload } = action

        if (payload) {
          return {
            ...state,
            ...payload,
          }
        }

        return state
      },
      [CLEAR_SCHOOL_WIDGET_SCHOOLS]: () => ({}),
    },
    {}
  ),
  selectedDistrictId: handleActions<SelectedDistrict, PayloadAction<SelectedDistrict>>(
    {
      [SELECT_DISTRICT]: payloadReducer,
      [CLEAR_SCHOOL_WIDGET_DATA]: nullReducer,
    },
    null
  ),
  selectedSchoolLevel: handleActions<SchoolLevelsWidget, PayloadAction<SchoolLevelsWidget>>(
    {
      [SELECT_SCHOOL_LEVEL]: payloadReducer,
      [CLEAR_SCHOOL_WIDGET_DATA]: () => 'elementary',
    },
    'elementary'
  ),
  districtMeta: districtMetaReducer,
  schoolsMeta: schoolsMetaReducer,
})

export default schoolWidgetReducer
